/**
 * Which person fills which pane of the native call stage.
 *
 * Panes are matched by the role each endpoint announces
 * (`malkaVisualRole`, visual-language-media-policy/roles.ts), never by who
 * joined first: before this, an invited guest who joined a VRI session ahead
 * of the interpreter took the Interpreter pane, and everyone after the
 * second person was dropped from a VRI stage altogether.
 *
 * Nobody is left out. The named panes (client, interpreter, hearing party)
 * take only a person whose role fits them; everyone else in the room (team
 * interpreters, a second Deaf participant, invited guests, the other signers
 * in a group call) comes back in `others` and gets a tile of their own.
 *
 * Pure, so the rules can be tested without React Native.
 */
import { findParticipantByHint } from '../visual-language-media-policy/roles';
import { normalizeRoomName } from './peerWaiting';
/**
 * Which rules a room is laid out by.
 *
 * @param {string} [roomName] - The conference room name.
 * @param {boolean} vriApp - Whether this is a VRI app build.
 * @returns {RoomPaneKind}
 */
export function getRoomPaneKind(roomName, vriApp) {
    const room = normalizeRoomName(roomName);
    // Checked first, as the media policy does: a group call is a group call
    // whichever app joined it.
    if (room.startsWith('p2p-') || room.startsWith('group-')) {
        return 'platform';
    }
    if (vriApp || room.startsWith('vri-') || room.startsWith('instant-')) {
        return 'vri';
    }
    return 'vrs';
}
/**
 * Assigns the room's people to panes.
 *
 * @param {Object} options - The room.
 * @param {RoomPaneKind} options.kind - Which rules apply (getRoomPaneKind).
 * @param {P} [options.local] - The local participant.
 * @param {LayoutRole} [options.localRole] - The local person's call role (stored `vrs_user_role`).
 * @param {P[]} options.remote - Visible remote participants, in join order.
 * @param {Object} options.roleById - Remote roles: inferRemoteRoles for interpreted
 * rooms, inferPlatformRoomRoles for platform rooms.
 * @param {string} [options.targetClient] - The stored `vrs_target_client` hint.
 * @returns {IRoomPaneAssignment<P>}
 */
export function assignRoomPanes({ kind, local, localRole, remote, roleById, targetClient }) {
    const remaining = remote.slice();
    const roleOf = (participant) => roleById[participant.id] || (kind === 'platform' ? 'client' : 'guest');
    const withRole = (role) => remaining.filter(participant => roleOf(participant) === role);
    const take = (participant) => {
        const index = participant ? remaining.indexOf(participant) : -1;
        if (index >= 0) {
            remaining.splice(index, 1);
        }
        return participant;
    };
    const takeClient = (candidates) => take(findParticipantByHint(candidates, targetClient) || candidates[0]);
    const assignment = { others: remaining };
    if (kind === 'platform') {
        const signers = () => remaining.filter(participant => roleOf(participant) !== 'interpreter');
        if (localRole === 'interpreter') {
            assignment.interpreter = local;
            assignment.client = takeClient(signers());
        }
        else if (localRole === 'client') {
            assignment.client = local;
            assignment.interpreter = take(signers()[0]);
        }
        else {
            assignment.client = takeClient(signers());
        }
        return assignment;
    }
    if (localRole === 'interpreter') {
        assignment.interpreter = local;
        assignment.client = takeClient(withRole('client'));
    }
    else if (localRole === 'client') {
        assignment.client = local;
        assignment.interpreter = take(withRole('interpreter')[0]);
    }
    else {
        assignment.interpreter = take(withRole('interpreter')[0]);
        assignment.client = takeClient(withRole('client'));
    }
    if (kind === 'vrs') {
        // Someone with no call role in a relay room is the hearing party.
        // Otherwise the hearing party is a guest: a second interpreter or
        // client is never shown as them.
        assignment.hearing = localRole ? take(withRole('guest')[0]) : local;
    }
    return assignment;
}
